import { FC, ReactNode, useContext, useMemo } from 'react'
import { generatePath, Link, useNavigate } from 'react-router'

import { Card, Flex, theme, Typography } from 'antd'

import Color from 'color'

import placeholderLight from '../../components/icon/assets/rider-light.png'
import placeholderDark from '../../components/icon/assets/rider.png'
import { Country } from '../../components/icon/Country'
import { Title } from '../../components/text/Title'
import { Context } from '../../providers/Config'
import routes from '../../routes'

import { RiderNumber } from './RiderNumber'
import { TagsMeta } from './TagsMeta'

const { useToken } = theme

const IMAGE_HEIGHT = 360 as const
const PADDING = 16 as const

const RiderCard: FC<
  RiderCardFragment & {
    action?: ReactNode
  }
> = ({ slug, country, stats, photo, riderNumber, teamColor, career, deathDate, action, name }) => {
  const { token } = useToken()
  const lastAssignment: MotoGP.RiderCareer = career?.[0]

  const background = useMemo(() => {
    const riderTeamColor = teamColor ?? career?.find((it: MotoGP.RiderCareer) => !!it?.team?.color)?.team?.color
    return riderTeamColor ? Color(riderTeamColor).fade(0.8).hexa() : token.colorBgContainerDisabled
  }, [teamColor, token.colorBgContainerDisabled, career])

  const { theme } = useContext(Context)
  const placeholder = theme === 'light' ? placeholderLight : placeholderDark
  const link = generatePath(routes.rider, { slug })
  const navigate = useNavigate()

  return (
    <Card
      style={{ background, borderColor: background }}
      styles={{
        body: { padding: `0 ${PADDING}px ${PADDING}px` },
        cover: { padding: 1 },
      }}
      cover={
        <img
          onClick={() => navigate(link)}
          loading={'lazy'}
          style={{
            cursor: 'pointer',
            objectFit: 'cover',
            objectPosition: 'top',
            filter: deathDate ? 'grayscale(100%)' : undefined,
          }}
          height={IMAGE_HEIGHT}
          src={photo?.formats?.small?.url ?? photo?.url ?? placeholder}
          alt={photo?.alternativeText ?? name}
        />
      }
    >
      <Flex style={{ position: 'relative', top: PADDING - IMAGE_HEIGHT, width: 40 }} justify={'space-between'}>
        <div style={{ height: 40 }}>{action}</div>
      </Flex>
      <Flex style={{ position: 'relative', top: -80 }} justify={'space-between'}>
        <TagsMeta all_races={stats?.all_races} />
      </Flex>
      <Flex vertical gap={8} style={{ marginTop: -PADDING * 3 }}>
        <Flex align={'center'} justify={'space-between'} style={{ width: '100%' }}>
          <Flex align={'center'}>
            <RiderNumber color={background} number={riderNumber ?? lastAssignment?.number} />
            <Title style={{ marginBottom: 0 }} ellipsis level={4}>
              <Link to={link}>{name}</Link>
            </Title>
          </Flex>
          {country && <Country country={country} height={24} />}
        </Flex>
        <Flex justify={'space-between'} align={'center'} style={{ minHeight: token.lineHeight * token.fontSize }}>
          <Typography.Text ellipsis>{lastAssignment?.team?.name ?? lastAssignment?.sponsored_team}</Typography.Text>
          <Typography.Text>{lastAssignment?.team?.constructor?.name}</Typography.Text>
        </Flex>
      </Flex>
    </Card>
  )
}

export { RiderCard }
